import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  defaultSettingsQuery,
  generalShellQuery,
  isGeneralTab,
  saveDefaultSettings,
  type GeneralOption,
  type GeneralTab,
} from '../../api/settingsGeneral'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → General: `version-3/settings/general/index.php` (bara) plus
 * `default_settings.php`, element cu element.
 *
 * O SINGURA FILA
 * --------------
 * Ecranul avea patru. "Events" si cele doua din `agent_messages/` au fost
 * scoase din portal la 26.9.8.5 si taiate si de aici; de ce, si de ce nu se pun
 * la loc, scrie in capul clasei `GeneralSettings`
 * (`v4/api/src/Settings/General/GeneralSettings.php`).
 *
 * Bara ramane bara, cu grupurile ei: impartirea vine de la server, in `group`,
 * si ecranul deseneaza un titlu nou de fiecare data cand `group` se schimba, cu
 * un despartitor inaintea fiecarui titlu in afara de primul. Asa nu trebuie sa
 * stie nimic despre ce grupuri exista, o fila la care omul n-are drept - deci
 * care nici nu vine - nu lasa in urma un titlu gol, si o fila noua are unde sa
 * intre. Acelasi tipar il au si Agent Install si RustDesk, tot cu o fila.
 *
 * "Landing Pages" din bara veche era comentat de mult; nu s-a portat.
 *
 * Listele de optiuni (limbi, monede, unitati) vin de la server, nu sunt scrise
 * a doua oara aici: ele sunt si regula dupa care salvarea e primita sau
 * refuzata, iar doua liste scrise in doua locuri se despart in timp.
 */

export function GeneralSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(generalShellQuery())

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isGeneralTab(tab)) return
    const first = shell.data.tabs.find((t) => !t.external)
    if (first) void navigate({ to: '/settings/general/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />

  const current = isGeneralTab(tab) ? tab : (shell.data.tabs.find((t) => !t.external)?.id ?? '')

  return (
    <>
      <nav className="od-tabbar">
        <ul className="od-tabbar-nav">
          {shell.data.tabs.map((item, index) => (
            <SidebarBlock key={item.id} tab={item} current={current} previous={shell.data.tabs[index - 1]} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </nav>
      <div className="page-content-wrapper">{current === 'default_settings' && <DefaultSettingsPanel />}</div>
    </>
  )
}

/** Titlul de sectiune apare o data, la prima fila a grupului; despartitorul, inaintea lui. */
function SidebarBlock({ tab, current, previous }: { tab: GeneralTab; current: string; previous?: GeneralTab }) {
  const opens = !previous || previous.group !== tab.group
  return (
    <>
      {opens && previous ? <li className="menu-block-divider"></li> : null}
      {opens ? <li className="menu-header">{tab.group}</li> : null}
      <SidebarTab tab={tab} current={current} />
    </>
  )
}

function SidebarTab({ tab, current }: { tab: GeneralTab; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const href = tab.external ? '/' + tab.href.replace(/^\//, '') : '/app/settings/general/' + tab.id
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (tab.external) return
    event.preventDefault()
    void navigate({ to: '/settings/general/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a id={tab.id} title={tab.title} href={href} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

/**
 * Ce s-a intamplat, spus cu vorbele SERVERULUI cand le are.
 *
 * Un refuz vine ca 400 cu mesajul lui ("Language must be one of: ..."). Aruncat,
 * ar trece fara sa spuna nimic; scris ca "Something went wrong", ar ascunde
 * exact partea care ajuta.
 */
function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/* -------------------------------------------------------------------------
 * Settings (default_settings.php)
 * ---------------------------------------------------------------------- */

function DefaultSettingsPanel() {
  const query = useQueryClient()
  const page = useQuery(defaultSettingsQuery())
  const [language, setLanguage] = useState('')
  const [currency, setCurrency] = useState('')
  const [temperature, setTemperature] = useState('')
  const [idleMinutes, setIdleMinutes] = useState('')
  const [sessionHours, setSessionHours] = useState('')

  useEffect(() => {
    if (!page.data) return
    setLanguage(page.data.language)
    setCurrency(page.data.currency)
    setTemperature(page.data.temperature)
    setIdleMinutes(String(page.data.idle_minutes))
    setSessionHours(String(page.data.session_hours))
  }, [page.data])

  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void saveDefaultSettings({ language, currency, temperature, idle_minutes: idleMinutes, session_hours: sessionHours })
      .then(() => {
        toast('General settings updated.', 'Success')
        void query.invalidateQueries({ queryKey: ['settings', 'general', 'default-settings'] })
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div id="content">
      <h4>
        <strong>Settings</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Service provider default settings
      </label>
      <hr />
      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ marginBottom: 20 }}>General settings (default)</h5>
          </div>
          <div className="col-md-10">
            <form method="post" id="general_settings_form" onSubmit={submit}>
              <Choice name="language" label="Language" value={language} options={page.data.languages} onPick={setLanguage} />
              <Choice name="currency" label="Currency" value={currency} options={page.data.currencies} onPick={setCurrency} />
              <Choice name="temperature" label="Temperature" value={temperature} options={page.data.temperatures} onPick={setTemperature} />
              {/*
               * Cele doua ceasuri de securitate, ca in `default_settings.php`
               * de pe cec-master (26.9.10.1). Textele sunt ale machetei, cuvant
               * cu cuvant; plafoanele vin de la server (`idle_limits`), nu se
               * scriu a doua oara aici. O valoare in afara lor se INCADREAZA la
               * salvare (`Idle::clampMinutes`), nu se refuza.
               */}
              <hr className="dotted" />
              <div className="form-group row">
                <div className="col-md-6">
                  <label>Sign out after inactivity (minutes)</label>
                  <input
                    type="number"
                    name="idle_minutes"
                    className="form-control"
                    min={page.data.idle_limits.minutes.min}
                    max={page.data.idle_limits.minutes.max}
                    step={1}
                    value={idleMinutes}
                    onChange={(event) => setIdleMinutes(event.target.value)}
                  />
                  <span className="help-block">
                    Counted from the last keystroke or click, not from the last request the page made on its own. The
                    person is not logged out - they get a bar at the bottom of the page and come back with an emailed
                    code.
                  </span>
                </div>
                <div className="col-md-6">
                  <label>Sign in again after (hours)</label>
                  <input
                    type="number"
                    name="session_hours"
                    className="form-control"
                    min={page.data.idle_limits.hours.min}
                    max={page.data.idle_limits.hours.max}
                    step={1}
                    value={sessionHours}
                    onChange={(event) => setSessionHours(event.target.value)}
                  />
                  <span className="help-block">
                    The hard ceiling on the second factor, however busy the day was: reached, it is a full sign-in
                    again. The session cookie itself is fixed at 8 hours in the server configuration, so a value above
                    8 is capped by that in practice.
                  </span>
                </div>
              </div>
              <div className="aright">
                <button type="submit" className="btn btn-primary btn-sm" id="general_settings_button">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
        <hr className="dotted" />
      </section>
    </div>
  )
}

function Choice({
  name,
  label,
  value,
  options,
  onPick,
}: {
  name: string
  label: string
  value: string
  options: GeneralOption[]
  onPick(next: string): void
}) {
  return (
    <div className="form-group row">
      <div className="col-md-6">
        <label>{label}</label>
        <select name={name} className="form-control" value={value} onChange={(e) => onPick(e.target.value)}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
